import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './ticketDetail.css';
import './tickets.css';
import { createTicket } from './apiTickets';

// Validation schema
const ticketValidationSchema = Yup.object().shape({
  title: Yup.string()
    .min(5, 'Title must be at least 5 characters')
    .max(255, 'Title must not exceed 255 characters')
    .required('Title is required'),
  description: Yup.string()
    .min(20, 'Description must be at least 20 characters')
    .required('Description is required'),
  priority: Yup.string()
    .oneOf(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'], 'Invalid priority')
    .required('Priority is required'),
  issue_type: Yup.string()
    .oneOf(['HARDWARE', 'SOFTWARE', 'GENERAL_QUESTION', 'FEATURE_REQUEST', 'OTHER'], 'Invalid issue type')
    .required('Issue type is required'),
});

const CreateTicket = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBack = () => {
    navigate('/user-dashboard/tickets');
  };

  const handleSubmit = async (values, { setErrors }) => {
    try {
      setIsSubmitting(true);
      const createdTicket = await createTicket(values);
      toast.success('Ticket created successfully');
      // Navigate to the newly created ticket detail page
      navigate(`/user-dashboard/tickets/${createdTicket.id}`);
    } catch (error) {
      console.error('Error creating ticket:', error);
      setIsSubmitting(false);

      if (error.response && error.response.data) {
        // Handle validation errors from backend
        const backendErrors = error.response.data;
        if (typeof backendErrors === 'object') {
          setErrors(backendErrors);
        } else if (backendErrors.error) {
          toast.error(backendErrors.error);
        } else {
          toast.error('Failed to create ticket');
        }
      } else {
        toast.error('Failed to create ticket. Please try again.');
      }
    }
  };

  const initialValues = {
    title: '',
    description: '',
    priority: 'MEDIUM',
    issue_type: 'OTHER',
  };

  return (
    <div className="ticket-detail-container">
      {/* Header */}
      <div className="ticket-detail-header">
        <div className="ticket-detail-title-section">
          <button className="back-button" onClick={handleBack}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to Tickets
          </button>
          <h1 className="ticket-detail-title">Create New Ticket</h1>
        </div>
      </div>

      {/* Form */}
      <div className="detail-section" style={{ maxWidth: '800px' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={ticketValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isValid }) => (
            <Form className="ticket-form">
              {/* Title Field */}
              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  Title <span className="required">*</span>
                </label>
                <Field
                  id="title"
                  name="title"
                  type="text"
                  placeholder="Brief summary of your issue"
                  className={`form-input ${
                    errors.title && touched.title ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                />
                <ErrorMessage name="title" component="div" className="error-message" />
              </div>

              {/* Description Field */}
              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description <span className="required">*</span>
                </label>
                <Field
                  id="description"
                  name="description"
                  as="textarea"
                  placeholder="Detailed description of your issue. Please provide as much information as possible."
                  rows="8"
                  className={`form-textarea ${
                    errors.description && touched.description ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="error-message"
                />
              </div>

              {/* Issue Type Field */}
              <div className="form-group">
                <label htmlFor="issue_type" className="form-label">
                  Issue Type <span className="required">*</span>
                </label>
                <Field
                  id="issue_type"
                  name="issue_type"
                  as="select"
                  className={`form-select ${
                    errors.issue_type && touched.issue_type ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="HARDWARE">Hardware - Physical device issues</option>
                  <option value="SOFTWARE">Software - Application or system issues</option>
                  <option value="GENERAL_QUESTION">General Question - How-to or information</option>
                  <option value="FEATURE_REQUEST">Feature Request - New functionality</option>
                  <option value="OTHER">Other - Miscellaneous</option>
                </Field>
                <ErrorMessage name="issue_type" component="div" className="error-message" />
                <p className="form-hint">
                  Select the category that best describes your issue
                </p>
              </div>

              {/* Priority Field */}
              <div className="form-group">
                <label htmlFor="priority" className="form-label">
                  Priority <span className="required">*</span>
                </label>
                <Field
                  id="priority"
                  name="priority"
                  as="select"
                  className={`form-select ${
                    errors.priority && touched.priority ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="LOW">Low - General inquiry</option>
                  <option value="MEDIUM">Medium - Standard issue</option>
                  <option value="HIGH">High - Urgent issue</option>
                  <option value="CRITICAL">Critical - System down</option>
                </Field>
                <ErrorMessage name="priority" component="div" className="error-message" />
                <p className="form-hint">
                  Choose the appropriate priority level for your issue
                </p>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting || !isValid}
                >
                  {isSubmitting ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Helper Info */}
      <div className="info-section" style={{ maxWidth: '800px', marginTop: '2rem' }}>
        <h3 className="info-title">Tips for Creating a Ticket</h3>
        <ul style={{ color: '#aab7c4', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
          <li>Do not upload images or documents that contain sensative data</li>
          <li>Be specific and descriptive in your title</li>
          <li>Include error messages, screenshots, or relevant details</li>
          <li>Explain steps to reproduce the issue if applicable</li>
          <li>Choose the appropriate priority level</li>
          <li>You can add attachments after creating the ticket</li>
        </ul>
      </div>
    </div>
  );
};

export default CreateTicket;
